import { useState } from "react";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { X, DollarSign, AlertCircle } from "lucide-react";
import { useDispatch } from "react-redux";
import { customCaptureAmount } from "../../store/thunk/adminThunk";
import toast from "react-hot-toast";
import { fetchOrdersDetailsById } from "../../store/thunk/ordersThunk";
import {
  formatToUSDate,
  encodeToBase64,
} from "../../hooks/commonFunction";

const CustomCaptureAmountModal = ({ open, setOpen }) => {
  const [orderId, setOrderId] = useState();
  const [orderDetails, setOrderDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showNoOrder, setShowNoOrder] = useState(false);
  const dispatch = useDispatch();

  const handleSubmit = async () => {
    if (!orderId) {
      toast.error("Please enter an Order ID");
      return;
    }

    setIsLoading(true);
    setShowNoOrder(false);
    setOrderDetails(null);
    dispatch(fetchOrdersDetailsById(orderId))
      .unwrap()
      .then((response) => {
        if (response.status !== 0) {
          setOrderDetails(response);
          setIsLoading(false);
        } else {
          setOrderDetails(null);
          setIsLoading(false);
        }
      })
      .catch((err) => {
        setOrderDetails(null);
      });
  };

  const handleConfirmRecapture = async () => {
    if (!orderDetails) {
      toast.error("No order details available");
      return;
    }

    try {
      setIsLoading(true);

      // Make API call to confirm recapture payment
      const resultAction = await dispatch(
        customCaptureAmount({
          action: 'custom-capture-amount',
          order_id: orderId,
        })
      );

      if (customCaptureAmount.fulfilled.match(resultAction)) {
        const response = resultAction.payload;

        if (response?.status === 1) {
          toast.success("Payment recapture confirmed successfully");
          handleClose();
        } else {
          const errorMessage =
            response?.message || "Failed to confirm recapture payment";
          toast.error(errorMessage);
        }
      } else if (customCaptureAmount.rejected.match(resultAction)) {
        const errorMessage =
          resultAction.payload || "Failed to confirm recapture payment";
        toast.error(errorMessage);
        console.error("Recapture confirmation failed:", resultAction.payload);
      }
    } catch (error) {
      toast.error("An unexpected error occurred");
      console.error("Unexpected error during recapture:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
    // Reset state when closing
    setOrderId("");
    setOrderDetails(null);
    setShowNoOrder(false);
    setIsLoading(false);
  };

  const handleOrderIdChange = (e) => {
    setOrderId(e.target.value);
    // Reset states when user changes input
    setOrderDetails(null);
    setShowNoOrder(false);
  };

  return (
    <Dialog open={open} onClose={setOpen} className="relative z-50">
      <DialogBackdrop className="fixed inset-0 bg-black/50" />

      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto">
        <DialogPanel className="bg-white rounded-lg shadow-xl max-w-xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6 bg-red-gradient p-4">
            <DialogTitle className="text-md font-normal text-white flex items-center space-x-3">
              <DollarSign className="h-6 w-6" />
              <span>Custom Capture Amount</span>
            </DialogTitle>
            <button
              onClick={handleClose}
              className="text-white transition-colors hover:text-gray-200"
            >
              <X size={24} />
            </button>
          </div>

          {/* Content */}
          <div className="px-5">
            {/* Order ID Input - Show only when no order details are loaded */}
            {!orderDetails && !showNoOrder && (
              <div className="mb-8">
                <label className="block text-sm font-semibold text-gray-800 mb-3">
                  Order ID
                </label>
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={orderId}
                    onChange={handleOrderIdChange}
                    placeholder="Enter Order ID"
                    className="flex-1 border-2 border-gray-200 py-3 px-4 shadow-sm text-sm focus:outline-none focus:border-red-500 focus:ring-2 focus:ring-red-100 rounded-lg transition-all duration-200 bg-gray-50 hover:bg-white"
                  />
                  <button
                    onClick={handleSubmit}
                    disabled={!orderId || isLoading}
                    className={`px-6 py-3 rounded-lg font-semibold text-sm tracking-wide transition-all duration-300 transform whitespace-nowrap ${
                      orderId && !isLoading
                        ? "bg-red-gradient text-white hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] shadow-md"
                        : "bg-gray-200 text-gray-400 cursor-not-allowed"
                    }`}
                  >
                    <span className="flex items-center justify-center">
                      {isLoading ? (
                        <>
                          <svg
                            className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Processing...
                        </>
                      ) : (
                        "Search"
                      )}
                    </span>
                  </button>
                </div>
              </div>
            )}

            {/* Order Details Table - Show when order is found */}
            {orderDetails && (
              <div className="mb-6">
                <div className="bg-white border-2 border-gray-100 rounded-xl shadow-sm overflow-hidden">
                  {/* Table Header */}
                  <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-4 py-3 border-b border-gray-200">
                    <h3 className="text-sm font-semibold text-gray-700">
                      Order Information
                    </h3>
                  </div>

                  {/* Table Rows */}
                  <div className="divide-y divide-gray-100">
                    <div className="grid grid-cols-2 hover:bg-gray-50 transition-colors">
                      <div className="p-4 text-sm font-medium text-gray-600">
                        User Details
                      </div>
                      <div className="p-4 text-sm text-gray-800">
                        <div className="space-y-1">
                          <div className="font-medium text-gray-900">
                            {orderDetails.order_details?.name}
                          </div>
                          <div className="text-gray-600">
                            {
                              orderDetails.order_details?.phone_number
                            }
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 hover:bg-gray-50 transition-colors">
                      <div className="p-4 text-sm font-medium text-gray-600">
                        Order Date
                      </div>
                      <div className="p-4 text-sm text-gray-800">
                        {formatToUSDate(
                          orderDetails?.order_details?.created_at
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 hover:bg-gray-50 transition-colors">
                      <div className="p-4 text-sm font-medium text-gray-600">
                        Order ID
                      </div>
                      <div className="p-4 text-sm text-gray-800 font-mono">
                        {orderDetails?.payment_info?.payment_id ? (
                          <a
                            href={`view-order-details/${encodeToBase64(
                              orderDetails?.payment_info?.payment_id
                            )}`}
                            className="text-primary-100 hover:text-primary-100 underline transition-colors duration-200"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            #{orderDetails?.payment_info?.payment_id}
                          </a>
                        ) : (
                          orderDetails?.payment_info?.payment_id
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 hover:bg-gray-50 transition-colors">
                      <div className="p-4 text-sm font-medium text-gray-600">
                        Delivery Date
                      </div>
                      <div className="p-4 text-sm text-gray-800">
                        {formatToUSDate(
                          orderDetails?.order_details?.pickup_date
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 hover:bg-gray-50 transition-colors">
                      <div className="p-4 text-sm font-medium text-gray-600">
                        Amount
                      </div>
                      <div className="p-4 text-md font-bold text-red-700">
                        {orderDetails?.payment_info?.formatted_total_paid}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Confirm Recapture Button */}
                <div className="mt-6 flex justify-center">
                  <button
                    onClick={handleConfirmRecapture}
                    disabled={isLoading}
                    className={`px-6 py-2.5 rounded-lg font-medium text-sm transition-all duration-200 ${
                      isLoading
                        ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                        : "bg-red-gradient text-white hover:shadow-md hover:scale-105 active:scale-95 shadow-sm"
                    }`}
                  >
                    <span className="flex items-center space-x-2">
                      {isLoading ? (
                        <>
                          <svg
                            className="animate-spin h-4 w-4"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          <span>Processing...</span>
                        </>
                      ) : (
                        <span>Confirm Recapture</span>
                      )}
                    </span>
                  </button>
                </div>
              </div>
            )}

            {/* No Order Available State */}
            {showNoOrder && (
              <div className="mb-6">
                <div className="text-center py-8">
                  <AlertCircle className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-600 mb-2">
                    No Order Available
                  </h3>
                  <p className="text-sm text-gray-500 mb-4">
                    The order ID "{orderId}" could not be found in our system.
                  </p>
                  <button
                    onClick={() => {
                      setShowNoOrder(false);
                      setOrderId("");
                    }}
                    className="bg-gray-100 text-gray-700 py-2 px-4 rounded-lg font-medium hover:bg-gray-200 transition-colors duration-200"
                  >
                    Try Another Order ID
                  </button>
                </div>
              </div>
            )}
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
};

export default CustomCaptureAmountModal;
